import { DeleteOutline, LocalHospital, Mail, MedicalInformation, OpenInNew, Phone, Work } from '@mui/icons-material';
import { Autocomplete, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { fhirClient } from 'src/App';
import CustomModal, {
  CustomModalBasicProps,
  GridItem,
  GridSection,
} from 'src/components/CustomModal';
import InfoRibbon, { InfoTypography } from 'src/components/InfoRibbon';
import SearchTextField from 'src/components/SearchTextField';
import useTranslations from 'src/careflow/ph-core/internationalization/hook/useTranslations';
import {
  Bundle,
  Coding,
  FhirResource,
  Organization,
  Practitioner,
  PractitionerRole,
  Reference,
} from 'src/careflow/ph-core/fhir-client/resources';
import { cleanSearchInput } from 'src/utils/string';
import StethoscopeIcon from 'src/assets/icons/stethoscope.svg';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { getReferenceId, getResourcesAndRelatedResources } from 'src/utils/fhir';
import {
  PractitionerWrapper,
  WrappedPractitioner,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import { formatUSAddress } from 'src/utils/address';
import { debounce } from 'src/utils/timers';
import { usePatients, useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import useNotiMessage from 'src/hooks/useNotiMessage';
import { CRS_PATH } from 'src/routes/paths';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { isArray, isEqual, isString } from 'lodash';
import { GeneralPractitionerGrid } from 'src/careflow/helpers/types';
import { fetchOrganizationAffiliation } from 'src/careflow/helpers/common';
import { isActiveCondition } from 'src/careflow/helpers/constants';
import CellRow from 'src/careflow/common/CellRow';
import { CustomLoadingButton } from 'src/careflow/common/CustomButton';
import CustomLink from 'src/careflow/common/CustomLink';
import { getChildOrganizations } from '../../case/detail/components/SearchMember/api';

interface AssignGeneralPractitionerModalProps extends CustomModalBasicProps {
  patient: WrappedPatient | null;
}

const iconSize: CSSProperties = { height: 15, width: 15 };
const iconColor: CSSProperties['color'] = '#919eab';

const noResultsImage = require('src/assets/no_results.png');

const AssignGeneralPractitionerModal = ({
  onClose,
  patient,
  open,
  ...modalProps
}: AssignGeneralPractitionerModalProps) => {
  const { componentsData, configurations } = useTenantConfigData();
  const { practitioner: practitionerTenantData } = componentsData ?? {};
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const translate = useTranslations(`patients.generalPractitioners`);

  const [searchTextField, setSearchTextField] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [roleValue, setRoleValue] = useState<Coding | null>(null);
  const [generalPractitioners, setGeneralPractitioners] = useState<GeneralPractitionerGrid[]>([]);
  const [patientGeneralPractitioners, setPatientGeneralPractitioners] = useState<Reference[]>([]);

  const [_, { update: updatePatient }] = usePatients({
    autofetch: false,
  });

  const [rolesCodesVs] = useValueSet('ph-roles-codes', {
    map: ValueSetWrapper,
  });

  const { showErrorMessage, showSuccessMessage } = useNotiMessage();

  const [filterType, setFilterType] = useState<string | null>('name');
  const [allowedOrganizations, setAllowedOrganizations] = useState<any[]>([]);
  const [internalExternal, setInternalExternal] = useState<string[]>(['internal']);
  const [organizationInternalExternal, setOrganizationInternalExternal] = useState<string[]>([]);

  const authUser = useAuth().getCurrentUser();
  const user = useAuth();

  const userOrganizations = useMemo(
    () => authUser?.organizations ?? [],
    [authUser?.organizations]
  );

  const userOrgFhirId = useMemo(() => {
    if (user) {
      const selectedOrg = user.getSelectedOrg();
      if (
        selectedOrg &&
        selectedOrg?.fhir_uri &&
        isString(selectedOrg?.fhir_uri) &&
        selectedOrg?.fhir_uri.includes('Organization/')
      )
        try {
          return selectedOrg?.fhir_uri.split('Organization/')?.[1];
        } catch (error) {
          return null;
        }
    }
    return null;
  }, [user]);

  useEffect(() => {
    const getAllowedOrganizations = async () => {
      if (!userOrgFhirId) return;
      try {
        const result = await getChildOrganizations([userOrgFhirId]);
        if (result && isArray(result)) {
          setAllowedOrganizations(result);
        }
      } catch (error) {
        console.error('Error fetching child organizations:', error);
      }
    };

    getAllowedOrganizations();
  }, [userOrgFhirId]);

  useEffect(() => {
    const getOrgsInternalExternal = async () => {
      if (!internalExternal?.length || internalExternal.includes('all')) {
        setOrganizationInternalExternal([]);
        return;
      }

      if (!allowedOrganizations.length) {
        return setOrganizationInternalExternal([]);
      }

      const checkedInternal = internalExternal.includes('internal');
      const checkedExternal = internalExternal.includes('external');

      let result: string[] = [];

      if (checkedExternal && allowedOrganizations.length > 0) {
        try {
          const affiliations = await fetchOrganizationAffiliation(allowedOrganizations);
          result = affiliations;
        } catch (error) {
          console.error('Error fetching affiliations:', error);
        }
      }

      if (checkedInternal) {
        result = [...result, ...allowedOrganizations];
      }

      setOrganizationInternalExternal(Array.from(new Set(result)));
    };

    getOrgsInternalExternal();
  }, [internalExternal, allowedOrganizations.length]);

  const getPractitionerRoles = useCallback(
    async (searchString: string) => {
      try {
        setIsLoading(true);
        const cleansedSearch = searchString ? cleanSearchInput(searchString) : '';
        const splittedSearch = cleansedSearch ? cleansedSearch.split(' ') : [];
        const givenName = splittedSearch?.[0] ?? '';
        const familyName = splittedSearch.slice(1).join(' ');

        const filtersByName = {
          ...(givenName && familyName
            ? {
                'practitioner.given': givenName,
                ...(familyName
                  ? {
                      'practitioner.family': familyName,
                    }
                  : {}),
              }
            : {
                'practitioner.name': cleansedSearch,
              }),
        };

        const filterByIdentifier = {
          'practitioner.identifier': cleansedSearch,
        };

        const filterByPractitionerRoleCode = {
          'role': roleValue?.code,
        };

        const organizationFilter: { organization?: string } = {};
        if (!internalExternal.includes('all')) {
          if (organizationInternalExternal?.length > 0) {
            organizationFilter.organization = organizationInternalExternal.join(',');
          } else if (userOrganizations.length > 0 && internalExternal?.includes('internal')) {
            organizationFilter.organization = userOrganizations.join(',');
          }
        }

        const urlSearchParams = new URLSearchParams({
          ...(filterType === 'npi' && cleansedSearch ? { ...filterByIdentifier } : {}),
          ...(filterType === 'name' && cleansedSearch ? { ...filtersByName } : {}),
          ...organizationFilter,
          ...(filterType === 'role' && roleValue?.code ? { ...filterByPractitionerRoleCode } : {}),
          ...isActiveCondition,
          _count: String(50),
        }).toString();

        const prResponse = await fhirClient.search<Bundle<PractitionerRole>>(
          `PractitionerRole?_include=PractitionerRole:practitioner&_include=PractitionerRole:organization&${urlSearchParams}`
        );

        const resourceEntries =
          prResponse?.entry?.reduce?.<FhirResource[]>((resources, { resource }) => {
            if (!resource)
              return resources;
            return [...resources, resource];
          }, []) ?? [];

        const [practitionerRoles, relatedResources] = getResourcesAndRelatedResources(
          resourceEntries,
          'PractitionerRole'
        );

        const generalPractitioners = (practitionerRoles as PractitionerRole[]).reduce<
          GeneralPractitionerGrid[]
        >((generalPractitioners, practitionerRole) => {
          // Find practitioner if reference exists
          let practitioner = practitionerRole?.practitioner?.reference
            ? relatedResources.find(
                ({ id, resourceType }) =>
                  id === getReferenceId(practitionerRole?.practitioner?.reference) &&
                  resourceType === 'Practitioner'
              )
            : undefined;
          if (practitioner) practitioner = PractitionerWrapper(practitioner as Practitioner);

          // Find organization if reference exists
          const organization = practitionerRole?.organization?.reference
            ? (relatedResources.find(
                ({ id, resourceType }) =>
                  id === getReferenceId(practitionerRole?.organization?.reference) &&
                  resourceType === 'Organization'
              ) as Organization)
            : undefined;

          // Support roles with just practitioner, just organization, or both
          return [
            ...generalPractitioners,
            {
              id: practitionerRole.id as string,
              npi: (practitioner as WrappedPractitioner)?.getNPI?.(),
              internal: (practitioner as WrappedPractitioner)?.getInternalIdenfitier?.(configurations?.provider),
              role: practitionerRole?.code?.[0],
              practitioner: practitioner
                ? {
                    id: practitioner?.id,
                    fullName: (practitioner as WrappedPractitioner)?.getFullName?.(),
                  }
                : undefined,
              organization: organization
                ? {
                    id: organization?.id,
                    fullName: organization?.name,
                  }
                : undefined,
              phone: (practitioner as WrappedPractitioner)?.getPhones?.()?.[0]?.value,
              email: (practitioner as WrappedPractitioner)?.getEmails?.()?.[0]?.value,
              address: formatUSAddress(
                (practitioner as WrappedPractitioner)?.getPrimaryAddress?.()
              ),
            },
          ];
        }, []);
        setGeneralPractitioners(generalPractitioners);
      } catch (error) {}
      setIsLoading(false);
    },
    [filterType, roleValue?.code, configurations, internalExternal, organizationInternalExternal]
  );

  const handleSearchInputChange = useCallback(
    debounce((value: string) => {
      setSearchFilter(value);
    }, 600),
    []
  );

  useEffect(() => {
    if (!userOrganizations || userOrganizations.length === 0) return;
    getPractitionerRoles(searchFilter);
  }, [searchFilter, filterType, roleValue?.code, internalExternal, organizationInternalExternal]);

  const handleClearFields = useCallback(() => {
    setSearchTextField('');
    setSearchFilter('');
    setFilterType('name');
    setPatientGeneralPractitioners([]);
    setRoleValue(null);
    setInternalExternal(['internal']);
  }, []);

  useEffect(() => {
    if (!open) return;
    handleClearFields();
    if (patient?.generalPractitioner && patient?.generalPractitioner?.length > 0) 
      setPatientGeneralPractitioners(patient?.generalPractitioner);
  }, [open, patient, handleClearFields]);

  const handleGeneralPractitionerAssigment = (
    isAdding: boolean,
    generalPractitioner: GeneralPractitionerGrid
  ) => {
    if (isAdding) {
      setPatientGeneralPractitioners([
        ...patientGeneralPractitioners,
        {
          reference: `PractitionerRole/${generalPractitioner.id}`,
          display: generalPractitioner?.practitioner?.fullName ?? undefined,
        },
      ]);
      return;
    }
    setPatientGeneralPractitioners(
      patientGeneralPractitioners.filter(
        ({ reference }) => getReferenceId(reference) !== generalPractitioner.id
      )
    );
  };

  const disabledSaveButton = useMemo(
    () => isEqual(patient?.generalPractitioner ?? [], patientGeneralPractitioners),
    [patient?.generalPractitioner, patientGeneralPractitioners]
  );

  const handleOnSave = async () => {
    if (!patient) return;
    try {
      setIsSaving(true);
      await updatePatient({
        ...patient,
        generalPractitioner: [
          ...patientGeneralPractitioners,
        ],
      });

      showSuccessMessage(translate('successMessage.linkGeneralPractitioner'));
      onClose?.({}, 'backdropClick');
    } catch (error) {
      showErrorMessage(translate('errorMessage.linkGeneralPractitioner'));
    }
    setIsSaving(false);
  };

  const filterTypes: Coding[] = [
    { code: 'npi', display: `${translate('npi')} OR ${translate('internal')}`},
    { code: 'name', display: translate('name') },
    { code: 'role', display: translate('role') }
  ];

  const filterTypeSelected = filterTypes.find(({ code }) => code === filterType);

  const gridColumns: GridColDef<GeneralPractitionerGrid>[] = [
    {
      flex: 1,
      sortable: false,
      field: 'npi',
      headerName: translate('practitioner'),
      renderCell: (params) => {
        const { npi, internal } = params.row;

        return (
          <Box>
            <CellRow
              tooltipTitle={translate('npi')}
              shouldTruncateText={false}
              title={npi ?? ''}
              Icon={<MedicalInformation sx={{ ...iconSize }} htmlColor={iconColor} />}
            />
            <CellRow
              tooltipTitle={translate('internal')}
              shouldTruncateText={false}
              title={internal ?? ''}
              Icon={<Work sx={{ ...iconSize }} htmlColor={iconColor} />}
            />
          </Box>
        )
      },
    },
    {
      flex: 1,
      sortable: false,
      field: 'practitionerAndOrg',
      headerName: translate('practitionerAndOrg'),
      renderCell: (params) => {
        const { practitioner, organization } = params.row;
        return (
          <Box>
            {!!practitioner?.fullName && (
              <CellRow
                tooltipTitle={translate('practitioner')}
                shouldTruncateText={false}
                title={practitioner?.fullName}
                Icon={
                  practitionerTenantData?.list?.practitionerName?.icon ? (
                    <Iconify 
                      icon={practitionerTenantData.list.practitionerName.icon} 
                      sx={{
                        ...iconSize,
                        color: '#637381'
                      }} 
                    />
                  ) : (
                    <StethoscopeIcon
                      style={{
                        ...iconSize
                      }}
                    />
                  )
                }
              />
            )}
            {!!organization?.fullName && (
              <CellRow
                tooltipTitle={translate('organization')}
                Icon={<LocalHospital sx={iconSize} htmlColor={iconColor} />}
                shouldTruncateText={false}
                title={organization?.fullName}
              />
            )}
          </Box>
        );
      },
    },
    {
      flex: 1,
      sortable: false,
      field: 'role',
      headerName: translate('role'),
      renderCell: (params) => {
        const { role } = params.row;
        return (
          <CellRow shouldTruncateText={false} title={role?.coding?.[0]?.display ?? role?.text} />
        );
      },
    },
    {
      flex: 1,
      sortable: false,
      field: 'contactDetails',
      headerName: translate('contactDetails'),
      renderCell: (params) => {
        const { phone, email } = params.row;
        return (
          <Box>
            <CellRow
              tooltipTitle={translate('phone')}
              shouldTruncateText={false}
              title={phone}
              Icon={<Phone sx={{ ...iconSize }} htmlColor={iconColor} />}
            />
            <CellRow
              tooltipTitle={translate('email')}
              shouldTruncateText={false}
              title={email}
              Icon={<Mail sx={{ ...iconSize }} htmlColor={iconColor} />}
            />
          </Box>
        );
      },
    },
    {
      flex: 1,
      sortable: false,
      field: 'address',
      headerName: translate('address'),
      renderCell: (params) => {
        const { address } = params.row;
        return (
          <CellRow
            shouldTruncateText={false}
            titleSx={{
              whiteSpace: 'pre-line',
              lineHeight: 1.5,
            }}
            title={address}
          />
        );
      },
    },
    {
      flex: 0.3,
      field: 'action',
      sortable: false,
      align: 'center',
      headerName: ' ',
      renderCell: (params) => {
        const { id } = params.row;
        const isAdded = !!patientGeneralPractitioners.find(
          ({ reference }) => getReferenceId(reference) === id
        );

        return (
          <CustomLoadingButton
            loading={isSaving}
            disabled={isSaving}
            color={!isAdded ? 'primary' : 'error'}
            onClick={() => {
              handleGeneralPractitionerAssigment(!isAdded, params.row);
            }}
          >
            {!isAdded ? translate('link') : translate('unlink')}
          </CustomLoadingButton>
        );
      },
    },
  ];

  return (
    <CustomModal
      open={open}
      title={translate('linkExistingPractitioner')}
      breadcrumbs={[
        translate('patient'),
        patient?.getFullName?.() ?? '',
        translate('groups'),
        translate('linkExistingPractitioner'),
      ]}
      isSaveButtonDisabled={disabledSaveButton}
      saveButtonTooltip={!patientGeneralPractitioners.length ? translate('mustAddOneToSave') : ''}
      saveButtonTitle={translate('save')}
      onSave={handleOnSave}
      onClose={onClose}
      onCancel={onClose as Function}
      isLoading={isSaving}
      containerSx={[{ overflow: 'scroll', width: '70vw', minWidth: 800 }]}
      childrenWithoutPadding={
        <Box mt={3}>
          {!!patient && (
            <InfoRibbon containerSx={{ marginTop: 2 }}>
              <InfoTypography>{`${translate('patient')} ${patient?.getFullName?.()}`}</InfoTypography>
              {!!patient?.birthDate && (
                <InfoTypography>
                  Date of Birth {patient?.getBirthDateForDisplay?.()} ({patient?.getAgeInYears?.()}{' '}
                  years)
                </InfoTypography>
              )}

              {!!patient?.getMRN?.(configurations?.mpi?.systemMrn)?.value && (
                <InfoTypography>MRN {patient.getMRN(configurations?.mpi?.systemMrn)?.value}</InfoTypography>
              )}
              {!!patient?.getPrimaryPhone?.()?.value && (
                <InfoTypography>Phone {patient.getPrimaryPhone()?.value}</InfoTypography>
              )}
              {!!patient?.getPrimaryEmail?.()?.value && (
                <InfoTypography>Email {patient.getPrimaryEmail()?.value}</InfoTypography>
              )}
            </InfoRibbon>
          )}
        </Box>
      }
      {...modalProps}
    >
      <>
        <GridSection spacing={2}>
          <GridItem xs={2.8}>
            <FormControl fullWidth>
              <InputLabel> {translate('searchBy')}</InputLabel>
              <Select
                disabled={!!isLoading}
                value={filterType}
                label={translate('searchBy')}
                onChange={(event) => {
                  setFilterType(event.target.value);
                }}
              >
                {filterTypes.map(({ code, display }) => (
                  <MenuItem key={`filter-type-${code}`} value={code}>
                    {display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridItem>

          <GridItem xs={9} display={'flex'} justifyContent={'row'} alignItems={'center'}>
            {filterTypeSelected?.code === 'role' ? (
              <Autocomplete
                sx={{ width: 400 }}
                disabled={!rolesCodesVs?.asListAll?.()?.length}
                value={roleValue}
                onChange={(_: React.SyntheticEvent, role) => {
                  setRoleValue(role);
                }}
                options={rolesCodesVs?.asListAll?.() ?? []}
                getOptionLabel={(role: Coding) => role?.display as string}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={'Role'}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {!rolesCodesVs?.asListAll?.()?.length ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ): (
              <SearchTextField
                value={searchTextField}
                onChange={(event) => {
                  const { value } = event.target;
                  setSearchTextField(cleanSearchInput(value));
                  handleSearchInputChange(value);
                }}
                placeholder={`${translate('searchBy')} ${
                  filterTypeSelected ? filterTypeSelected.display : ''
                }`}
                sx={{ width: 400 }}
              />
            )}
            
            <FormControl component="fieldset" sx={{ ml: 2 }}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={internalExternal.includes('internal')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setInternalExternal((prev) => {
                          const filtered = prev.filter(v => v !== 'all' && v !== 'internal');
                          if (checked) {
                            return [...filtered, 'internal'];
                          }
                          return filtered.length === 0 ? ['all'] : filtered;
                        });
                      }}
                      disabled={isLoading || isSaving}
                    />
                  }
                  label="Internal"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={internalExternal.includes('external')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setInternalExternal((prev) => {
                          const filtered = prev.filter(v => v !== 'all' && v !== 'external');
                          if (checked) {
                            return [...filtered, 'external'];
                          }
                          return filtered.length === 0 ? ['all'] : filtered;
                        });
                      }}
                      disabled={isLoading || isSaving}
                    />
                  }
                  label="Affiliated"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={internalExternal.includes('all')}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setInternalExternal(['all']);
                        } else {
                          setInternalExternal([]);
                        }
                      }}
                      disabled={isLoading || isSaving}
                    />
                  }
                  label="All"
                />
              </FormGroup>
            </FormControl>

            <Button
              disabled={isLoading || isSaving}
              sx={{
                ml: 2,
                paddingY: 2,
                paddingX: 3,
                alignSelf: 'center',
                color: '#ff5630',
                width: 78,
                height: 26,
                border: 0,
                '&:hover': { border: 0, backgroundColor: 'rgba(255, 86, 48, 0.16)' },
              }}
              onClick={() => {
                handleClearFields();
              }}
              variant="outlined"
              color="inherit"
            >
              <DeleteOutline htmlColor={'#ff5630'} sx={{ mr: '1px' }} />
              <Typography
                color={'#ff5630'}
                fontSize={'0.9rem'}
                fontWeight={'bold'}
                sx={{ mr: '2px' }}
              >
                {translate('clearFields')}
              </Typography>
            </Button>
          </GridItem>
        </GridSection>
        <GridSection
          marginTop={2}
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '50vh',
            overflow: 'hidden',
            overflowY: 'scroll',
            // justifyContent="flex-end" # DO NOT USE THIS WITH 'scroll'
          }}
        >
          <GridItem xs={12}>
            {!!generalPractitioners.length || isLoading ? (
              <DataGrid
                loading={isLoading}
                getRowSpacing={() => ({ bottom: 1, top: 2 })}
                rows={generalPractitioners}
                getRowHeight={() => 'auto'}
                columns={gridColumns}
                autoHeight={true}
                className="dataGridWithFilter"
                sx={[
                  { 
                    width: '100%',
                    '& .MuiDataGrid-row': {
                      borderBottom: '1px solid #EAECF0',
                      '&:nth-of-type(odd)': {
                        backgroundColor: '#FAFAFA',
                      },
                      '&:hover': {
                        backgroundColor: '#F5F6F7',
                      },
                    },
                  }
                ]}
                isRowSelectable={() => false}
                disableColumnFilter
                disableColumnMenu
                components={{
                  Footer: () => null,
                  Pagination: () => null,
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: 300,
                  flexDirection: 'column',
                }}
              >
                <img
                  style={{
                    width: 213,
                    height: 160,
                  }}
                  src={noResultsImage}
                  alt="noResultsImage"
                />
                <Typography sx={{ textAlign: 'center', marginTop: 2 }} variant="h4">
                  {translate('noResultsFound')}
                </Typography>
                <CustomLink
                  to="#"
                  onClick={() => {
                    window.open(`${CRS_PATH.provider.list}`, '_blank');
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {translate('doYouWantToCreateAPractitioner')}
                    <OpenInNew sx={{ marginLeft: 1 }} />
                  </Box>
                </CustomLink>
              </Box>
            )}
          </GridItem>
        </GridSection>
      </>
    </CustomModal>
  );
};

export default AssignGeneralPractitionerModal;
